/**
 * Skeleton components for Budget Builder page
 * These MUST match the exact dimensions of the actual components to prevent CLS
 *
 * Key principles:
 * 1. Same structure (entity tabs, tier sections, card grid, lock banner)
 * 2. Same row/card heights (same padding)
 * 3. Same number of rows
 * 4. Minimal animation (only animate a few elements to reduce jank)
 */

import { Lock } from "lucide-react";
import { m } from "@/paraglide/messages";

// Static skeleton placeholder - no animation to reduce jank
function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`bg-muted/40 rounded ${className}`} aria-hidden="true" />
  );
}

// Only the first card gets animation to indicate loading
function AnimatedSkeleton({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-muted/50 rounded ${className}`}
      aria-hidden="true"
    />
  );
}

// ─── Desktop card skeleton ────────────────────────────────────────────────────
// Matches BudgetBuilderDesktopCard exactly:
//   [color bar | identity w-44 | bars flex-1 | history w-44]

function DesktopCardSkeleton({ isFirst = false }: { isFirst?: boolean }) {
  const S = isFirst ? AnimatedSkeleton : Skeleton;
  return (
    <div className="flex items-stretch rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Team color bar */}
      <S className="w-1 shrink-0" />

      {/* Identity: w-44 px-4 py-3 */}
      <div className="flex flex-col justify-center px-4 py-3 w-44 shrink-0 gap-1.5">
        <S className="h-4 w-16" />
        <S className="h-3 w-28" />
        <S className="h-4 w-12" />
      </div>

      {/* Probability bars: flex-1, 4 rows (Tier A columns) */}
      <div className="flex-1 flex flex-col justify-center px-4 py-3 gap-1 min-w-0">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <S className="h-3 w-10 shrink-0" />
            <S className="flex-1 h-3.5" />
            <S className="h-3 w-8 shrink-0" />
          </div>
        ))}
      </div>

      {/* History + delta: w-44 px-4 py-3 */}
      <div className="flex flex-col justify-center px-4 py-3 w-44 shrink-0 border-l border-border/50 gap-1.5">
        <S className="h-3 w-32" />
        <S className="h-3 w-20" />
        <S className="h-4 w-16" />
      </div>
    </div>
  );
}

// ─── Mobile card skeleton ─────────────────────────────────────────────────────
// Matches BudgetDriverCard exactly:
//   header (color bar | name/code | price/xPts)
//   bars (4 rows for Tier A)
//   footer (R-2/R-1 | xΔ$)

function MobileCardSkeleton({ isFirst = false }: { isFirst?: boolean }) {
  const S = isFirst ? AnimatedSkeleton : Skeleton;
  return (
    <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
      {/* Header: px-4 py-3 */}
      <div className="flex items-center gap-3 px-4 py-3">
        <S className="w-1 h-10 rounded-full shrink-0" />
        <div className="flex-1 min-w-0 space-y-1">
          <S className="h-4 w-20" />
          <S className="h-3 w-28" />
        </div>
        <div className="text-right shrink-0 space-y-1">
          <S className="h-4 w-14 ml-auto" />
          <S className="h-3 w-16 ml-auto" />
        </div>
      </div>

      {/* Bars: px-4 pb-2 space-y-1 — 4 rows for Tier A */}
      <div className="px-4 pb-2 space-y-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <S className="h-3 w-10 shrink-0" />
            <S className="flex-1 h-4" />
            <S className="h-3 w-8 shrink-0" />
          </div>
        ))}
      </div>

      {/* Footer: px-4 py-2 border-t */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-border/50">
        <S className="h-3 w-24" />
        <S className="h-3 w-20" />
      </div>
    </div>
  );
}

// ─── Lock banner skeleton ─────────────────────────────────────────────────────
// Matches the actual lock banner exactly — uses real static content so height
// is identical; no need to skeleton the text.

function LockBannerSkeleton({ compact = false }: { compact?: boolean }) {
  if (compact) {
    // Mobile version
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-center">
        <Lock className="h-5 w-5 mx-auto mb-2 text-primary/70" />
        <p className="text-sm font-semibold mb-2">
          {m.f1_budget_unlock_tier_a()}
        </p>
        <p className="text-xs text-muted-foreground mb-3">
          {m.f1_budget_unlock_tier_a_desc()}
        </p>
        <div className="flex gap-2 justify-center">
          <div className="h-8 w-28 rounded-md bg-primary/30" />
          <div className="h-8 w-20 rounded-md bg-muted/50 border border-border" />
        </div>
      </div>
    );
  }
  // Desktop version
  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row items-center gap-4">
      <div className="flex items-center gap-3 flex-1">
        <Lock className="h-5 w-5 text-primary/70 shrink-0" />
        <div>
          <p className="text-sm font-semibold">{m.f1_budget_unlock_tier_a()}</p>
          <p className="text-xs text-muted-foreground">
            {m.f1_budget_unlock_tier_a_desc_drivers()}
          </p>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <div className="h-8 w-28 rounded-md bg-primary/30" />
        <div className="h-8 w-20 rounded-md bg-muted/50 border border-border" />
      </div>
    </div>
  );
}

// ─── Desktop content skeleton ─────────────────────────────────────────────────
// Matches desktop layout: entity tabs → Tier A (lock + 6 cards) → Tier B (14 cards)

export function BudgetBuilderDesktopContentSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Entity tabs — same height as actual px-4 py-2 buttons */}
      <div className="flex gap-2">
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 border border-primary/40 text-sm font-medium">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-14" />
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50 border border-border text-sm font-medium">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>

      {/* Tier A section */}
      <div className="space-y-3">
        {/* Section heading */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-36 rounded-full" />
        </div>
        {/* Lock banner */}
        <LockBannerSkeleton />
        {/* 2-column card grid — 6 drivers */}
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <DesktopCardSkeleton key={i} isFirst={i === 0} />
          ))}
        </div>
      </div>

      {/* Tier B section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-5 w-36 rounded-full" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 14 }).map((_, i) => (
            <DesktopCardSkeleton key={i} isFirst={i === 0} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Mobile content skeleton ──────────────────────────────────────────────────
// Matches mobile layout: lock banner → 6 BudgetDriverCard skeletons

export function BudgetBuilderMobileContentSkeleton() {
  return (
    <div className="px-4 py-4 space-y-3">
      {/* Lock banner for Tier A */}
      <LockBannerSkeleton compact />
      {/* Single-column card stack — 6 drivers */}
      {Array.from({ length: 6 }).map((_, i) => (
        <MobileCardSkeleton key={i} isFirst={i === 0} />
      ))}
    </div>
  );
}

// ─── Legacy export (still used as desktop fallback) ───────────────────────────
/** @deprecated Use BudgetBuilderDesktopContentSkeleton or BudgetBuilderMobileContentSkeleton */
export { BudgetBuilderDesktopContentSkeleton as BudgetBuilderContentSkeleton };
